/**
 * Message backfill policy for chat rooms.
 *
 * Focus-group intros are generated asynchronously after room create and emitted
 * over Socket.IO. Clients that hydrate before those events (or join the socket
 * room after they fire) can briefly see an empty message list. A single early
 * REST backfill is not enough — empty is ambiguous for the first few seconds.
 *
 * Cache scope: one-shot, not cached (pure scheduling helpers).
 */

/** Delays (ms) between REST backfill attempts after the socket join effect runs. */
export const MESSAGE_BACKFILL_RETRY_DELAYS_MS = [500, 1500, 3000, 5000] as const

/**
 * Returns the delay before the next backfill attempt, or null when retries are exhausted.
 * `attempt` is 0-based and counts completed attempts (including empty results).
 */
export function getMessageBackfillDelayMs(attempt: number): number | null {
  if (attempt < 0 || attempt >= MESSAGE_BACKFILL_RETRY_DELAYS_MS.length) {
    return null
  }
  return MESSAGE_BACKFILL_RETRY_DELAYS_MS[attempt]
}

/**
 * Whether another REST backfill should be scheduled after an empty or failed fetch.
 * Empty and error share the same retry budget so late intros still land; once the
 * client already has messages, never retry.
 */
export function shouldRetryMessageBackfill(opts: {
  messagesEmpty: boolean
  hasLoadedInitialMessages: boolean
  attemptJustCompleted: number
}): boolean {
  if (!opts.hasLoadedInitialMessages) {
    return false
  }
  if (!opts.messagesEmpty) {
    return false
  }
  return getMessageBackfillDelayMs(opts.attemptJustCompleted + 1) != null
}
